// SPEC-qam-backlog Session 11 — per-user popup (toast) suppression settings.
// SPEC-1 senior-prefs (2026-09-23) — the same page grows two role-gated
// sections: "Senior & Admin" (auto-advance, Expected/Actual table, queue
// counts) and "Saved filters" (rename / delete only; filters are created from
// the list view in Phase 2). Agents keep the notification switches only (D8).
//
// All-user surface (route /settings, sidebar item "Preferences"). Preferences
// are stored server-side (keyed by the session's user) so they survive page
// refresh and re-login. Every write is one PATCH of the changed fields; saved
// filters are always written as the whole list (§5).

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Bell, Loader2, Pencil, Trash2, Check, X, ShieldCheck, Filter } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useRole } from "@/hooks/use-role";
import { isUnauthorizedError, redirectToLogin } from "@/lib/auth-utils";
import {
  NOTIFICATION_GROUP_META,
  DEFAULT_USER_PREFS,
  SAVED_FILTER_NAME_MAX,
  type SavedFilter,
  type UserPrefs,
} from "@shared/notification-preferences";
import {
  NOTIFICATION_PREFS_QUERY_KEY,
  useNotificationPrefs,
} from "@/hooks/use-notification-toasts";

interface SeniorSwitchMeta {
  key: "autoAdvance" | "showExpectedActual" | "showQueueFooter";
  testId: string;
  label: string;
  description: string;
}

const SENIOR_SWITCHES: SeniorSwitchMeta[] = [
  {
    key: "autoAdvance",
    testId: "switch-auto-advance",
    label: "Auto-advance after a decision",
    description: "Default on. When off, the page stays on the delivery after Agree, Good or Skip.",
  },
  {
    key: "showExpectedActual",
    testId: "switch-show-expected-actual",
    label: "Show Expected / Actual table",
    description:
      "Default off. Shows the model comparison table and Model diagnostics on the detail page. Bounding boxes stay on either way.",
  },
  {
    key: "showQueueFooter",
    testId: "switch-show-queue-footer",
    label: "Show queue counts on the detail page",
    description:
      "Default off. A footer under the left column: — unseen, ✓ agent-verified awaiting senior, ● in progress by me. Always today.",
  },
];

const SORT_LABELS: Record<SavedFilter["sort"], string> = {
  completed_desc: "Newest first",
  completed_asc: "Oldest first",
  qa_completed_desc: "Latest QA first",
  qa_completed_asc: "Earliest QA first",
};

/** Chips summarising a saved filter's settings, in the list view's dropdown order. */
function filterChips(f: SavedFilter): string[] {
  const chips: string[] = [];
  if (f.agentStatus.length) chips.push(`Agent: ${f.agentStatus.join(", ")}`);
  if (f.owner.length) chips.push(`Owner: ${f.owner.join(", ")}`);
  if (f.driver.length) chips.push(`Driver: ${f.driver.join(", ")}`);
  if (f.match.length) chips.push(`Match: ${f.match.join(", ")}`);
  if (f.bag.length) chips.push(`Bag: ${f.bag.join(", ")}`);
  chips.push(`Sort: ${SORT_LABELS[f.sort] ?? f.sort}`);
  return chips;
}

export default function NotificationSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isSenior, isAdmin } = useRole();
  const isSeniorOrAdmin = isSenior || isAdmin;
  // isPlaceholderData: until the real fetch lands, `data` is the default
  // placeholder — render it as loading, NOT as the user's saved state, or a
  // refresh makes saved-off toggles flash ON and look like they were cleared.
  const { data, isLoading: isFetching, isPlaceholderData } = useNotificationPrefs();
  const isLoading = isFetching || isPlaceholderData;
  const prefs: UserPrefs = data ?? DEFAULT_USER_PREFS;

  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<SavedFilter | null>(null);

  const updateMutation = useMutation({
    mutationFn: async (patch: Partial<UserPrefs>) => {
      const res = await apiRequest("PATCH", "/api/user/notification-preferences", patch);
      return res.json() as Promise<UserPrefs>;
    },
    // Optimistic: flip the cached value immediately so the Switch responds with
    // no perceptible lag, then reconcile with the server response.
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: NOTIFICATION_PREFS_QUERY_KEY });
      const previous = queryClient.getQueryData<UserPrefs>(NOTIFICATION_PREFS_QUERY_KEY);
      queryClient.setQueryData<UserPrefs>(NOTIFICATION_PREFS_QUERY_KEY, {
        ...(previous ?? DEFAULT_USER_PREFS),
        ...patch,
      });
      return { previous };
    },
    onError: (error: Error, _patch, context) => {
      // Roll back the optimistic update.
      if (context?.previous) {
        queryClient.setQueryData(NOTIFICATION_PREFS_QUERY_KEY, context.previous);
      }
      if (isUnauthorizedError(error)) { redirectToLogin(toast as any); return; }
      toast({ title: "Couldn't save preference", description: error.message, variant: "destructive" });
    },
    onSuccess: (serverPrefs) => {
      queryClient.setQueryData(NOTIFICATION_PREFS_QUERY_KEY, serverPrefs);
    },
  });

  const busy = isLoading || updateMutation.isPending;

  const startRename = (f: SavedFilter) => {
    setRenamingId(f.id);
    setRenameValue(f.name);
  };
  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue("");
  };
  const commitRename = () => {
    if (!renamingId) return;
    const name = renameValue.trim();
    if (!name) { cancelRename(); return; }
    const clash = prefs.savedFilters.some((f) => f.id !== renamingId && f.name.toLowerCase() === name.toLowerCase());
    if (clash) {
      toast({ title: "Name already used", description: "Pick a different name for this filter.", variant: "destructive" });
      return;
    }
    // Rename issues a PATCH with the FULL list (§5).
    updateMutation.mutate({
      savedFilters: prefs.savedFilters.map((f) => (f.id === renamingId ? { ...f, name } : f)),
    });
    cancelRename();
  };
  const confirmDelete = () => {
    if (!deleteTarget) return;
    // Delete PATCHes the list without the row (§5).
    updateMutation.mutate({
      savedFilters: prefs.savedFilters.filter((f) => f.id !== deleteTarget.id),
    });
    setDeleteTarget(null);
  };

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Bell className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-xl font-semibold" data-testid="text-notification-settings-title">Preferences</h1>
          <p className="text-sm text-muted-foreground">
            Control which confirmation pop-ups appear while you review deliveries. These settings are personal to you.
          </p>
        </div>
      </div>

      <Card data-testid="card-notification-preferences">
        <CardHeader>
          <CardTitle className="text-base">Notifications</CardTitle>
          <CardDescription>
            Errors, end-of-queue notices, re-extraction status, and the early-delivery-risk alert always show and can’t be turned off.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {NOTIFICATION_GROUP_META.map((meta) => {
            const toggleId = `notify-${meta.group}`;
            return (
              <div key={meta.group} className="flex items-start justify-between gap-4">
                <div className="space-y-0.5">
                  <Label htmlFor={toggleId} className="text-sm font-medium" data-testid={`label-${toggleId}`}>
                    {meta.label}
                  </Label>
                  <p className="text-xs text-muted-foreground">{meta.description}</p>
                </div>
                <Switch
                  id={toggleId}
                  checked={prefs[meta.prefKey]}
                  disabled={busy}
                  onCheckedChange={(checked) => updateMutation.mutate({ [meta.prefKey]: checked })}
                  data-testid={`switch-${toggleId}`}
                />
              </div>
            );
          })}
          {busy && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="notification-prefs-saving">
              <Loader2 className="h-3 w-3 animate-spin" />
              {isLoading ? "Loading your preferences…" : "Saving…"}
            </div>
          )}
        </CardContent>
      </Card>

      {isSeniorOrAdmin && (
        <>
          <Card data-testid="card-senior-admin-preferences">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                Senior &amp; Admin
              </CardTitle>
              <CardDescription>How the QA detail page behaves for you. Personal, not shared.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {SENIOR_SWITCHES.map((sw) => (
                <div key={sw.key} className="flex items-start justify-between gap-4">
                  <div className="space-y-0.5">
                    <Label htmlFor={sw.testId} className="text-sm font-medium" data-testid={`label-${sw.testId}`}>
                      {sw.label}
                    </Label>
                    <p className="text-xs text-muted-foreground">{sw.description}</p>
                  </div>
                  {sw.key === "autoAdvance" && (
                    <Switch
                      id={sw.testId}
                      checked={prefs.autoAdvance}
                      disabled={busy}
                      onCheckedChange={(checked) => updateMutation.mutate({ autoAdvance: checked })}
                      data-testid="switch-auto-advance"
                    />
                  )}
                  {sw.key === "showExpectedActual" && (
                    <Switch
                      id={sw.testId}
                      checked={prefs.showExpectedActual}
                      disabled={busy}
                      onCheckedChange={(checked) => updateMutation.mutate({ showExpectedActual: checked })}
                      data-testid="switch-show-expected-actual"
                    />
                  )}
                  {sw.key === "showQueueFooter" && (
                    <Switch
                      id={sw.testId}
                      checked={prefs.showQueueFooter}
                      disabled={busy}
                      onCheckedChange={(checked) => updateMutation.mutate({ showQueueFooter: checked })}
                      data-testid="switch-show-queue-footer"
                    />
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          <Card data-testid="card-saved-filters">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                Saved filters
              </CardTitle>
              <CardDescription>New filters are saved from the list view. Rename or delete them here.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {prefs.savedFilters.length === 0 && (
                <p className="text-sm text-muted-foreground" data-testid="saved-filters-empty">No saved filters yet.</p>
              )}
              {prefs.savedFilters.map((f) => (
                <div
                  key={f.id}
                  className="flex items-start justify-between gap-3 rounded-md border p-3"
                  data-testid={`saved-filter-row-${f.id}`}
                >
                  <div className="min-w-0 flex-1 space-y-1.5">
                    {renamingId === f.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={renameValue}
                          maxLength={SAVED_FILTER_NAME_MAX}
                          autoFocus
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitRename();
                            if (e.key === "Escape") cancelRename();
                          }}
                          className="h-8"
                          data-testid={`input-rename-filter-${f.id}`}
                        />
                        <Button size="sm" variant="secondary" onClick={commitRename} disabled={busy} data-testid={`button-rename-save-${f.id}`}>
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={cancelRename} data-testid={`button-rename-cancel-${f.id}`}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="text-sm font-medium truncate" data-testid={`saved-filter-name-${f.id}`}>{f.name}</div>
                    )}
                    <div className="flex flex-wrap gap-1">
                      {filterChips(f).map((chip) => (
                        <Badge key={chip} variant="secondary" className="font-normal">{chip}</Badge>
                      ))}
                    </div>
                  </div>
                  {renamingId !== f.id && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Rename ${f.name}`}
                        onClick={() => startRename(f)}
                        disabled={busy}
                        data-testid={`button-rename-filter-${f.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Delete ${f.name}`}
                        onClick={() => setDeleteTarget(f)}
                        disabled={busy}
                        data-testid={`button-delete-filter-${f.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
            <AlertDialogContent data-testid="dialog-delete-filter">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this saved filter?</AlertDialogTitle>
                <AlertDialogDescription>
                  “{deleteTarget?.name}” will be removed from your list. This only affects you.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="button-delete-filter-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={confirmDelete} data-testid="button-delete-filter-confirm">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
    </div>
  );
}
